import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { TokenSeriesPoint } from '../../api/types';

interface Props {
  points: TokenSeriesPoint[];
  loading?: boolean;
}

const TOKENS_COLOR = '#6366f1';
const COST_COLOR = '#10b981';

function formatTokens(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString('en-US');
}

function formatUsd(n: number): string {
  if (n >= 1000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
}

export function TokensVsCostLineChart({ points, loading }: Props): JSX.Element {
  const dates = points.map((p) => p.date);

  const option: EChartsOption = {
    grid: { left: 64, right: 64, top: 56, bottom: 36 },
    legend: {
      top: 8,
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 12 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      valueFormatter: () => '',
      formatter: (params) => {
        const list = Array.isArray(params) ? params : [params];
        const first = list[0];
        if (!first || typeof first !== 'object' || !('axisValue' in first)) return '';
        const date = String(first.axisValue);
        const rows = list
          .map((p) => {
            if (typeof p !== 'object' || p === null) return '';
            const seriesName = 'seriesName' in p ? String(p.seriesName) : '';
            const raw = 'value' in p ? Number(p.value ?? 0) : 0;
            const color = 'color' in p ? String(p.color) : '#000';
            const formatted = seriesName === 'Cost (USD)' ? formatUsd(raw) : formatTokens(raw);
            return `
              <tr>
                <td style="padding:2px 8px 2px 0">
                  <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${color};margin-right:6px;vertical-align:middle"></span>${seriesName}
                </td>
                <td style="text-align:right;padding:2px 0;font-weight:600">${formatted}</td>
              </tr>`;
          })
          .join('');
        return `
          <div style="font-weight:600;margin-bottom:4px">${date}</div>
          <table style="font-size:12px;border-collapse:collapse">${rows}</table>`;
      },
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { formatter: (v: string) => v.slice(5) },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Tokens',
        position: 'left',
        nameLocation: 'middle',
        nameGap: 52,
        axisLine: { lineStyle: { color: TOKENS_COLOR } },
        axisLabel: { formatter: (v: number) => formatTokens(v) },
        splitLine: { show: true },
      },
      {
        type: 'value',
        name: 'Cost (USD)',
        position: 'right',
        nameLocation: 'middle',
        nameGap: 52,
        axisLine: { lineStyle: { color: COST_COLOR } },
        axisLabel: { formatter: (v: number) => formatUsd(v) },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: 'Total tokens',
        type: 'line',
        yAxisIndex: 0,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2.5, color: TOKENS_COLOR },
        itemStyle: { color: TOKENS_COLOR },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(99, 102, 241, 0.22)' },
              { offset: 1, color: 'rgba(99, 102, 241, 0.02)' },
            ],
          },
        },
        data: points.map((p) => p.totalTokens),
      },
      {
        name: 'Cost (USD)',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        symbol: 'diamond',
        symbolSize: 7,
        lineStyle: { width: 2.5, color: COST_COLOR },
        itemStyle: { color: COST_COLOR },
        data: points.map((p) => Number(p.costUsd.toFixed(4))),
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 320, width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
